import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";

import integrante1 from "../../static/media/nosotros/Integrante_5.webp";
import integrante2 from "../../static/media/nosotros/Integrante_4.webp";
import integrante3 from "../../static/media/nosotros/Integrante_3.webp";
import integrante4 from "../../static/media/nosotros/Integrante_2.webp";
import integrante5 from "../../static/media/nosotros/Integrante_1.webp";

const C = {
  darkBlue: "#0C1F2F",
  navy: "#0057A8",
  softGold: "#c2ed80",
  white: "#ffffff",
};

// Arreglo reordenado: Logística primero (1), Bodeguero al final (4)
const EQUIPO = [
  { id: 1, rol: "Logística", img: integrante1 },
  { id: 2, rol: "Ventas", img: integrante2 },
  { id: 5, rol: "Marketing", img: integrante5 },
  { id: 3, rol: "Ventas", img: integrante3 },
  { id: 4, rol: "Bodeguero", img: integrante4 },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

export default function NuestroEquipo() {
  return (
    <Box
      id="nuestro-equipo"
      sx={{
        background: C.darkBlue,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        pt: { xs: "80px", md: "100px" },
        pb: { xs: "25vw", md: "10vw" },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: "20px", sm: "40px", md: "56px" }, // Padding uniforme
          width: "100%",
          zIndex: 1,
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        {/* TÍTULO */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            textAlign: "center",
            mb: { xs: 6, md: 10 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: C.white,
              fontWeight: 750,
              fontSize: { xs: 28, sm: 34, md: 40 },
              letterSpacing: "-.02em",
              mb: 2,
            }}
          >
            Nuestro Equipo
          </Typography>
          <Box
            sx={{
              width: "50px",
              height: "3px",
              background: C.softGold,
              borderRadius: "2px",
              mb: 3,
            }}
          />
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              fontSize: { xs: 14, md: 16 },
              fontWeight: 500,
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            El talento humano y profesional detrás de cada proyecto exitoso.
          </Typography>
        </Box>

        {/* CONTENEDOR DE IMÁGENES */}
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: { xs: "20px", sm: "24px", md: "30px" }, // Espaciado controlado
          }}
        >
          {EQUIPO.map((miembro) => (
            <Box
              key={miembro.id}
              component={motion.div}
              variants={itemVariants}
              sx={{
                // Matemáticas exactas para asegurar el centrado en cualquier pantalla
                width: {
                  xs: "calc(50% - 10px)", // 2 por fila (Gap de 20px / 2 = 10px)
                  sm: "calc(33.333% - 16px)", // 3 por fila (Gap 24px)
                  md: "calc(20% - 24px)", // 5 por fila (Gap 30px)
                },
                maxWidth: "280px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "3/4",
                  borderRadius: "16px",
                  overflow: "hidden",
                  boxShadow: "0 15px 35px rgba(0,0,0,0.4)",
                  mb: 3,
                  position: "relative",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Box
                  component={motion.img}
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  src={miembro.img}
                  alt={`Equipo - ${miembro.rol}`}
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/300x400/0C1F2F/FFFFFF?text=Retrato";
                  }}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    cursor: "pointer",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "40%",
                    background:
                      "linear-gradient(180deg, transparent 0%, rgba(12,31,47,0.8) 100%)",
                    pointerEvents: "none",
                  }}
                />
              </Box>

              <Typography
                sx={{
                  color: C.white,
                  fontWeight: 700,
                  fontSize: { xs: 14, md: 16 },
                  textAlign: "center",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                {miembro.rol}
              </Typography>
              <Typography
                sx={{
                  color: C.softGold,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  mt: 0.5,
                  textAlign: "center",
                }}
              >
                Eléctrica GRM
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
